import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { ProgressSnapViewer } from "@/components/ProgressSnapViewer";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const SnapView = () => {
  const { t } = useTranslation();

  const { snapId } = useParams<{ snapId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [snap, setSnap] = useState<any>(null);

  useEffect(() => {
    const loadSnap = async () => {
      if (!snapId) {
        navigate("/");
        return;
      }

      try {
        const { data, error } = await supabase
          .from("progress_snaps")
          .select("*")
          .eq("id", snapId)
          .single();

        if (error || !data) {
          console.error("Error loading snap:", error);
          toast.error(t('notifications.toast.snap_not_found'));
          navigate("/");
          return;
        }

        // Check if snap owner has a public profile
        const { data: profileData } = await supabase
          .from("profiles")
          .select("is_public")
          .eq("id", data.user_id)
          .single();

        const isPublicProfile = profileData?.is_public === true;
        
        // Allow access if:
        // 1. Profile is public (anonymous or authenticated users can view)
        // 2. User is the owner
        // 3. User follows the owner
        if (!isPublicProfile) {
          // Profile is private - authentication required
          if (!user) {
            toast.error(t('notifications.toast.please_sign_in_to_view_this_snap'));
            navigate("/auth");
            return;
          }

          const isOwner = data.user_id === user.id;
          
          if (!isOwner) {
            // Check if user follows the owner
            const { data: followData } = await supabase
              .from("follows")
              .select("id")
              .eq("follower_id", user.id)
              .eq("following_id", data.user_id)
              .maybeSingle();

            if (!followData) {
              toast.error(t('notifications.toast.no_access_to_snap'));
              navigate("/");
              return;
            }
          }
        }

        setSnap(data);
        setLoading(false);
      } catch (error) {
        console.error("Error loading snap:", error);
        toast.error(t('notifications.toast.failed_to_load_snap'));
        navigate("/");
      }
    };

    loadSnap();
  }, [snapId, navigate, user]);

  const handleClose = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-white" />
      </div>
    );
  }

  if (!snap) {
    return null;
  }

  return (
    <ProgressSnapViewer
      snap={snap}
      onClose={handleClose}
      currentUserId={user?.id}
    />
  );
};

export default SnapView;
